import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const BulkOperations = ({ 
  selectedMilestones = [], 
  onBulkAction, 
  milestones = [],
  isMobile = false 
}) => {
  const [bulkAction, setBulkAction] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const bulkActions = [
    { value: 'complete', label: 'Mark as Complete', icon: 'Check', variant: 'success' },
    { value: 'delete', label: 'Delete Selected', icon: 'Trash2', variant: 'danger' },
    { value: 'updatePriority', label: 'Update Priority', icon: 'Flag', variant: 'default' },
    { value: 'updateAssignee', label: 'Change Assignee', icon: 'User', variant: 'default' },
    { value: 'enableNotifications', label: 'Enable Notifications', icon: 'Bell', variant: 'default' },
    { value: 'disableNotifications', label: 'Disable Notifications', icon: 'BellOff', variant: 'default' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'critical', label: 'Critical Priority' }
  ];

  const selectedMilestoneDetails = milestones?.filter(m => 
    selectedMilestones?.includes(m?.id)
  );

  const handleBulkAction = (action) => {
    if (action === 'delete' || action === 'complete') {
      setPendingAction(action);
      setShowConfirmation(true);
      return;
    }

    // For other actions, execute immediately
    onBulkAction?.(action, selectedMilestones);
    setBulkAction('');
  };

  const confirmBulkAction = () => {
    if (pendingAction) {
      onBulkAction?.(pendingAction, selectedMilestones);
      setPendingAction(null);
      setShowConfirmation(false);
      setBulkAction('');
    }
  };

  const cancelBulkAction = () => {
    setPendingAction(null);
    setShowConfirmation(false);
    setBulkAction('');
  };

  const getActionConfig = (actionValue) => {
    return bulkActions?.find(a => a?.value === actionValue);
  };

  if (selectedMilestones?.length === 0) {
    return null;
  }

  return (
    <div className={`bg-primary/5 border-b border-primary/20 ${isMobile ? 'p-4' : 'p-6'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Icon name="CheckSquare" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">
              {selectedMilestones?.length} milestone{selectedMilestones?.length !== 1 ? 's' : ''} selected
            </span>
          </div>

          {!isMobile && selectedMilestoneDetails?.length > 0 && (
            <div className="flex items-center space-x-3 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="CheckCircle" size={12} />
                <span>
                  {selectedMilestoneDetails?.filter(m => m?.status === 'completed')?.length} completed
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={12} />
                <span>
                  {selectedMilestoneDetails?.filter(m => m?.status === 'in-progress')?.length} in progress
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="AlertTriangle" size={12} />
                <span>
                  {selectedMilestoneDetails?.filter(m => m?.status === 'overdue')?.length} overdue
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {!showConfirmation ? (
            <>
              <Select
                placeholder="Choose action..."
                value={bulkAction}
                onValueChange={setBulkAction}
                className="w-48"
              >
                {bulkActions?.map(action => (
                  <option key={action?.value} value={action?.value}>
                    {action?.label}
                  </option>
                ))}
              </Select>

              <Button
                variant="default"
                size="sm"
                onClick={() => handleBulkAction(bulkAction)}
                disabled={!bulkAction}
                iconName={getActionConfig(bulkAction)?.icon}
              >
                Apply
              </Button>
            </>
          ) : (
            <div className="flex items-center space-x-2">
              <div className="text-sm text-foreground">
                {pendingAction === 'delete' 
                  ? `Delete ${selectedMilestones?.length} milestone${selectedMilestones?.length !== 1 ? 's' : ''}?`
                  : `Mark ${selectedMilestones?.length} milestone${selectedMilestones?.length !== 1 ? 's' : ''} as complete?`
                }
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={cancelBulkAction}
              >
                Cancel
              </Button>
              
              <Button
                variant={pendingAction === 'delete' ? 'danger' : 'success'}
                size="sm"
                onClick={confirmBulkAction}
                iconName={pendingAction === 'delete' ? 'Trash2' : 'Check'}
              >
                {pendingAction === 'delete' ? 'Delete' : 'Complete'}
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Selected Milestones Preview (Mobile) */}
      {isMobile && selectedMilestoneDetails?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-primary/20">
          <div className="space-y-2">
            {selectedMilestoneDetails?.slice(0, 3)?.map(milestone => (
              <div key={milestone?.id} className="flex items-center space-x-2 text-xs">
                <div className={`w-2 h-2 rounded-full ${
                  milestone?.status === 'completed' ? 'bg-success' :
                  milestone?.status === 'in-progress' ? 'bg-accent' :
                  milestone?.status === 'overdue' ? 'bg-error' : 'bg-muted-foreground'
                }`} />
                <span className="text-foreground truncate flex-1">
                  {milestone?.title}
                </span>
                <span className="text-muted-foreground">
                  {milestone?.priority}
                </span>
              </div>
            ))}
            {selectedMilestoneDetails?.length > 3 && (
              <div className="text-xs text-muted-foreground text-center">
                +{selectedMilestoneDetails?.length - 3} more selected
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkOperations;